import { useState, useRef } from "react";
import api from "../api";
import {
  ChevronDown,
  Camera, Mic, Pencil, Send, Square, Play, Trash2, X, Upload, Clock, Truck,
  BadgeCheck, ShieldCheck,
} from "lucide-react";
import UserLayout from "../components/Userlayout";
import {
  MAX_FILE_MB, MAX_PHOTOS, MAX_VOICE, MAX_FILE_BYTES, tooBig, isImageFile, filterFiles,
  pickAudioType, audioExt, uploadErrorMessage,
} from "../uploadUtils";

export default function MechanicHome() {
  const user = JSON.parse(localStorage.getItem("user") || "null");

  // ----- Vehicle (step 1) -----
  const [manualOpen, setManualOpen] = useState(false);
  const [idType, setIdType] = useState("VIN"); // "VIN" | "Rego"
  const [idValue, setIdValue] = useState("");
  const [vehicleImage, setVehicleImage] = useState(null); // File
  const [vehiclePreview, setVehiclePreview] = useState("");
  const [dragging, setDragging] = useState(false);
  const vehicleInputRef = useRef(null);

  // ----- Part (step 2) -----
  const [partPhotos, setPartPhotos] = useState([]); // File[]
  const [partPreviews, setPartPreviews] = useState([]); // string[]
  const [notes, setNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);
  const partInputRef = useRef(null);

  // ----- Voice recording -----
  const [recording, setRecording] = useState(false);
  const [voiceNotes, setVoiceNotes] = useState([]); // { blob, url }[]
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const [submitting, setSubmitting] = useState(false);

  // ---------- Vehicle image handlers ----------
  const handleVehicleFile = (file) => {
    if (!file) return;
    if (!isImageFile(file)) return alert("Please choose an image file for the vehicle / VIN plate.");
    if (tooBig(file)) return alert(`Vehicle photo must be ${MAX_FILE_MB} MB or smaller.`);
    if (vehiclePreview) URL.revokeObjectURL(vehiclePreview);
    setVehicleImage(file);
    setVehiclePreview(URL.createObjectURL(file));
  };
  const onVehicleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleVehicleFile(e.dataTransfer.files[0]);
  };

  // ---------- Part photos ----------
  const handlePartFiles = (fileList) => {
    const room = Math.max(0, MAX_PHOTOS - partPhotos.length);
    const { accepted: added, problems } = filterFiles(fileList, { room, accept: isImageFile, label: "photo" });
    if (problems.length) alert(problems.join("\n"));
    if (!added.length) return;
    setPartPhotos((prev) => [...prev, ...added]);
    setPartPreviews((prev) => [...prev, ...added.map((f) => URL.createObjectURL(f))]);
  };
  const removePartPhoto = (i) => {
    setPartPreviews((prev) => {
      if (prev[i]) URL.revokeObjectURL(prev[i]);
      return prev.filter((_, idx) => idx !== i);
    });
    setPartPhotos((prev) => prev.filter((_, idx) => idx !== i));
  };

  // ---------- Voice recording ----------
  const toggleRecording = async () => {
    if (recording) {
      mediaRecorderRef.current?.stop();
      return;
    }
    try {
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
        return alert("Audio recording is not supported in this browser.");
      }
      if (voiceNotes.length >= MAX_VOICE) return alert(`You can add up to ${MAX_VOICE} voice notes.`);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickAudioType();
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data?.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const type = mr.mimeType || mimeType || chunksRef.current[0]?.type || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        if (blob.size > 0 && blob.size <= MAX_FILE_BYTES) {
          setVoiceNotes((prev) => [...prev, { blob, url: URL.createObjectURL(blob), type }]);
        } else if (blob.size > MAX_FILE_BYTES) {
          alert(`Voice note must be ${MAX_FILE_MB} MB or smaller.`);
        }
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      alert("Microphone permission denied or unavailable.");
    }
  };
  const removeVoice = (i) => setVoiceNotes((prev) => {
    if (prev[i]?.url) URL.revokeObjectURL(prev[i].url);
    return prev.filter((_, idx) => idx !== i);
  });

  // ---------- Submit ----------
  const submit = async () => {
    // client-side validation mirrors backend
    if (!vehicleImage && !idValue.trim()) {
      alert("Add a vehicle photo or enter VIN/Rego.");
      return;
    }
    if (!notes.trim() && partPhotos.length === 0 && voiceNotes.length === 0) {
      alert("Tell us the part — record a voice note, add a photo, or type notes.");
      return;
    }

    if (recording) {
      alert("Stop the voice recording before submitting.");
      return;
    }

    const fd = new FormData();
    fd.append("vehicleMethod", vehicleImage ? "photo" : "manual");
    fd.append("idType", idType);
    if (idType === "VIN") fd.append("vin", idValue.trim());
    else fd.append("rego", idValue.trim());
    fd.append("notes", notes.trim());
    fd.append("customerContact", user?.email || user?.mobile || "");
    if (vehicleImage) fd.append("vehicleImage", vehicleImage);
    partPhotos.forEach((f) => fd.append("partPhotos", f));
    voiceNotes.forEach((v, i) => fd.append("voiceNotes", v.blob, `voice-${i + 1}.${audioExt(v.type || v.blob?.type)}`));

    setSubmitting(true);
    try {
      await api.post("/requests", fd, { timeout: 180000 });
      alert("Request submitted! We'll respond within 10 minutes.");
      // reset (and free the preview blobs)
      if (vehiclePreview) URL.revokeObjectURL(vehiclePreview);
      partPreviews.forEach((u) => URL.revokeObjectURL(u));
      voiceNotes.forEach((v) => v.url && URL.revokeObjectURL(v.url));
      setIdValue(""); setVehicleImage(null); setVehiclePreview("");
      setPartPhotos([]); setPartPreviews([]); setNotes(""); setShowNotes(false);
      setVoiceNotes([]); setManualOpen(false);
    } catch (err) {
      alert(uploadErrorMessage(err, "Something went wrong."));
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <UserLayout>
      <div className="min-h-screen bg-slate-50">


        {/* Body */}
        <div className="max-w-7xl mx-auto p-6 md:p-8">
          <div className="bg-white rounded-3xl shadow-sm p-6 md:p-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* LEFT */}
            <div className="space-y-6">
              {/* STEP 1 — Which Vehicle */}
              <section className="rounded-2xl bg-blue-50/60 p-6">
                <h3 className="flex items-center gap-2 font-bold text-slate-800">
                  <span className="grid place-items-center w-6 h-6 rounded-full bg-blue-700 text-white text-xs">1</span>
                  Which Vehicle?
                </h3>

                {/* Upload / drop zone */}
                <div
                  onClick={() => vehicleInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={onVehicleDrop}
                  className={`mt-4 cursor-pointer rounded-2xl border-2 border-dashed p-6 text-center transition ${dragging ? "border-blue-500 bg-blue-100/50" : "border-transparent"
                    }`}
                >
                  {vehiclePreview ? (
                    <div className="relative inline-block">
                      <img src={vehiclePreview} alt="vehicle" className="mx-auto max-h-40 rounded-xl object-contain" />
                      <button
                        onClick={(e) => { e.stopPropagation(); setVehicleImage(null); setVehiclePreview(""); }}
                        className="absolute -top-2 -right-2 bg-slate-800 text-white rounded-full p-1"
                      ><X size={12} /></button>
                    </div>
                  ) : (
                    <>
                      <div className="mx-auto w-16 h-16 rounded-full bg-blue-700 grid place-items-center shadow-lg">
                        <Camera className="text-white" />
                      </div>
                      <p className="mt-4 font-bold text-slate-800 leading-tight">
                        Take a picture of your<br />VIN plate / Registration
                      </p>
                      <p className="text-xs text-slate-500 mt-1">Fast way, snap it, get it done.</p>
                      <p className="text-[11px] text-blue-600 mt-1 flex items-center justify-center gap-1">
                        <Upload size={12} /> Drop image or click to upload
                      </p>
                    </>
                  )}
                  <input ref={vehicleInputRef} type="file" accept="image/*" hidden
                    onChange={(e) => { handleVehicleFile(e.target.files?.[0]); e.target.value = ""; }} />
                </div>

                <div className="text-center text-xs text-slate-400 my-2">OR</div>
                <div className="text-center">
                  <button onClick={() => setManualOpen((o) => !o)}
                    className="text-blue-700 font-semibold text-sm inline-flex items-center gap-1">
                    {manualOpen ? "Hide manual entry" : "Down manual entry option"}
                    <ChevronDown size={16} className={`transition ${manualOpen ? "rotate-180" : ""}`} />
                  </button>
                </div>

                {/* Manual VIN/Rego */}
                {manualOpen && (
                  <div className="mt-4 rounded-2xl border-2 border-cyan-300 bg-white p-4">
                    <div className="inline-flex rounded-full bg-slate-100 p-1 mb-3">
                      <button onClick={() => setIdType("VIN")}
                        className={`px-5 py-1.5 rounded-full text-sm font-bold ${idType === "VIN" ? "bg-cyan-400 text-white" : "text-slate-500"}`}>
                        VIN
                      </button>
                      <button onClick={() => setIdType("Rego")}
                        className={`px-5 py-1.5 rounded-full text-sm font-bold ${idType === "Rego" ? "bg-cyan-400 text-white" : "text-slate-500"}`}>
                        Rego
                      </button>
                    </div>
                    <input
                      value={idValue}
                      onChange={(e) => setIdValue(e.target.value)}
                      placeholder={idType === "VIN" ? "e.g. 1HGCM82633A004352" : "e.g. REG-82633A004351"}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    />
                  </div>
                )}
              </section>

              {/* STEP 2 — What part */}
              <section className="rounded-2xl bg-red-50/60 p-6">
                <h3 className="flex items-center gap-2 font-bold text-slate-800">
                  <span className="grid place-items-center w-6 h-6 rounded-full bg-orange-500 text-white text-xs">2</span>
                  What part do you need?
                </h3>

                {/* Mic */}
                <div className="text-center mt-4">
                  <button onClick={toggleRecording}
                    className={`mx-auto w-16 h-16 rounded-full grid place-items-center shadow-lg transition ${recording ? "bg-red-600 animate-pulse ring-4 ring-red-200" : "bg-red-500"
                      }`}>
                    {recording ? <Square className="text-white" /> : <Mic className="text-white" />}
                  </button>
                  <p className="mt-3 font-bold text-slate-800">
                    {recording ? "Recording... tap to stop" : "Tap to record a voice message"}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Tell us what part you need.</p>
                </div>

                {/* Voice notes list */}
                {voiceNotes.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {voiceNotes.map((v, i) => (
                      <div key={i} className="flex items-center gap-3 bg-white rounded-full px-3 py-2 border border-red-100">
                        <Play size={16} className="text-red-500 shrink-0" />
                        <audio src={v.url} controls className="h-8 flex-1" />
                        <button onClick={() => removeVoice(i)} className="text-slate-400 hover:text-red-500">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Photos / Add Notes buttons */}
                <div className="grid grid-cols-2 gap-3 mt-5">
                  <button onClick={() => partInputRef.current?.click()}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-full bg-white border border-green-200">
                    <span className="grid place-items-center w-9 h-9 rounded-full bg-green-600 text-white shrink-0"><Camera size={16} /></span>
                    <span className="text-left">
                      <span className="block text-xs font-bold text-slate-700">
                        {partPhotos.length > 0 ? `${partPhotos.length} Photo${partPhotos.length > 1 ? "s" : ""} added` : "Add Photos"}
                      </span>
                      <span className="block text-[10px] text-slate-400">Take or upload</span>
                    </span>
                  </button>
                  <button onClick={() => setShowNotes((s) => !s)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-full bg-white border ${showNotes ? "border-orange-400" : "border-orange-200"}`}>
                    <span className="grid place-items-center w-9 h-9 rounded-full bg-orange-500 text-white shrink-0"><Pencil size={16} /></span>
                    <span className="text-left">
                      <span className="block text-xs font-bold text-slate-700">Add Notes</span>
                      <span className="block text-[10px] text-slate-400">Type details</span>
                    </span>
                  </button>
                  <input ref={partInputRef} type="file" accept="image/*" multiple hidden
                    onChange={(e) => { handlePartFiles(e.target.files); e.target.value = ""; }} />
                </div>

                {/* Part photo thumbnails */}
                {partPreviews.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {partPreviews.map((src, i) => (
                      <div key={i} className="relative w-16 h-16">
                        <img src={src} alt="" className="w-16 h-16 object-cover rounded-lg border" />
                        <button onClick={() => removePartPhoto(i)}
                          className="absolute -top-1.5 -right-1.5 bg-slate-800 text-white rounded-full p-0.5">
                          <X size={10} />
                        </button>
                      </div>
                    ))}
                    <button onClick={() => partInputRef.current?.click()}
                      className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 grid place-items-center text-slate-400">
                      +
                    </button>
                  </div>
                )}

                {/* Notes textarea */}
                {showNotes && (
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder="What's wrong, or which part do you need? (optional)"
                    className="w-full mt-4 px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                )}
              </section>

              <button onClick={submit} disabled={submitting}
                className="mx-auto flex items-center gap-2 px-12 py-3.5 rounded-full bg-green-600 text-white font-semibold shadow-lg hover:bg-green-700 disabled:opacity-60">
                <Send size={18} /> {submitting ? "SUBMITTING..." : "SUBMIT REQUEST"}
              </button>
            </div>

            {/* RIGHT — marketing panel */}
            <div className="rounded-2xl bg-slate-50 p-6 md:p-8">
              <h2 className="text-2xl font-bold text-slate-800">Request a Part</h2>
              <p className="text-slate-500 mt-1">Tell us what you need and we'll find it for you.</p>
              <h3 className="mt-8 text-2xl font-bold text-slate-800">Need Used Auto Parts?</h3>
              <div className="text-4xl font-extrabold text-slate-800">
                We make it <span className="text-red-600">easy.</span>
              </div>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2 rounded-2xl bg-white p-4 grid place-items-center">
                  <div className="w-full aspect-video rounded-xl bg-slate-100 grid place-items-center text-slate-300 text-sm">
                    Transmission image
                  </div>
                </div>
                <div className="space-y-3">
                  {["Quality Used Transmissions", "Low Kilometer Engines", "Panels, Lights & More"].map((t) => (
                    <div key={t} className="flex items-center gap-3 rounded-2xl bg-white p-3 shadow-sm">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 shrink-0" />
                      <span className="font-bold text-slate-800 text-sm leading-tight">{t}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-6 bg-white rounded-2xl shadow p-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                {[
                  { icon: <Clock size={18} className="text-blue-500" />, t: "Response within", b: "10 minutes." },
                  { icon: <Truck size={18} className="text-red-500" />, t: "Australia", b: "wide delivery" },
                  { icon: <BadgeCheck size={18} className="text-green-600" />, t: "Quality", b: "Tested Parts" },
                  { icon: <ShieldCheck size={18} className="text-blue-500" />, t: "Trusted by", b: "2,000+ mechanics" },
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-2">
                    {f.icon}
                    <div><span className="block font-semibold text-slate-700">{f.t}</span><span className="block text-slate-500">{f.b}</span></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </UserLayout>
  );
}